import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useLanguage } from '@/state/language/LanguageProvider';

export type AlignmentTreeKind = 'vision' | 'objective' | 'strategy' | 'project';

export type HierarchicalVision = {
  vision_id: string;
  vision_name?: string;
  objectives?: Array<{
    objective_id: string;
    objective_name?: string;
    objective_kpi_id?: string | null;
    strategies?: Array<{
      strategy_id: string;
      strategy_name?: string;
      projects?: Array<{
        project_id: string;
        project_name?: string;
      }>;
    }>;
  }>;
};

export type AlignmentOrphanRow = {
  id: string;
  name: string;
};

export type AlignmentOrphans = {
  objectives?: AlignmentOrphanRow[];
  strategies?: AlignmentOrphanRow[];
  projects?: AlignmentOrphanRow[];
};

type TreeNode = {
  id: string;
  name: string;
  kind: AlignmentTreeKind;
  orphan: boolean;
  children: TreeNode[];
};

type AlignmentHierarchyTreeProps = {
  visions: HierarchicalVision[];
  orphans?: AlignmentOrphans | null;
};

const KIND_ORDER: AlignmentTreeKind[] = ['vision', 'objective', 'strategy', 'project'];

/**
 * Vision may have no parent. Project may have no children.
 * Orphan (red) = missing parent. Handicap (purple) = missing children.
 * Orphan wins when both apply.
 */
function isHandicapped(node: TreeNode): boolean {
  return node.kind !== 'project' && node.children.length === 0;
}

function nodeColor(node: TreeNode): string {
  if (node.orphan) {
    return 'text-[var(--color-error)]';
  }
  if (isHandicapped(node)) {
    return 'text-purple-700';
  }
  return 'text-[var(--color-surface-text)]';
}

function kindBadgeClass(kind: AlignmentTreeKind): string {
  if (kind === 'vision') {
    return 'bg-[var(--color-primary-bg)] text-[var(--color-primary-text)]';
  }
  if (kind === 'objective') {
    return 'bg-[var(--color-secondary-bg)] text-[var(--color-secondary-text)]';
  }
  if (kind === 'strategy') {
    return 'bg-[var(--color-accent-bg)]/15 text-[var(--color-accent-bg)]';
  }
  return 'border border-[var(--color-border)] text-[var(--color-secondary-text)]';
}

function collectIds(nodes: TreeNode[], acc: Set<string>): Set<string> {
  nodes.forEach((node) => {
    acc.add(`${node.kind}:${node.id}`);
    collectIds(node.children, acc);
  });
  return acc;
}

function buildLinkedForest(visions: HierarchicalVision[]): TreeNode[] {
  return (visions || []).filter(Boolean).map((vision) => ({
    id: vision.vision_id,
    name: vision.vision_name || '',
    kind: 'vision' as const,
    orphan: false,
    children: (vision.objectives || []).map((objective) => ({
      id: objective.objective_id,
      name: objective.objective_name || '',
      kind: 'objective' as const,
      orphan: false,
      children: (objective.strategies || []).map((strategy) => ({
        id: strategy.strategy_id,
        name: strategy.strategy_name || '',
        kind: 'strategy' as const,
        orphan: false,
        children: (strategy.projects || []).map((project) => ({
          id: project.project_id,
          name: project.project_name || '',
          kind: 'project' as const,
          orphan: false,
          children: []
        }))
      }))
    }))
  }));
}

function orphanNodes(orphans: AlignmentOrphans | null | undefined, linkedIds: Set<string>): TreeNode[] {
  const toNodes = (rows: AlignmentOrphanRow[] | undefined, kind: AlignmentTreeKind): TreeNode[] =>
    (rows || [])
      .filter((row) => row?.id && !linkedIds.has(`${kind}:${row.id}`))
      .map((row) => ({
        id: row.id,
        name: row.name || '',
        kind,
        orphan: true,
        children: []
      }));

  return [
    ...toNodes(orphans?.objectives, 'objective'),
    ...toNodes(orphans?.strategies, 'strategy'),
    ...toNodes(orphans?.projects, 'project')
  ];
}

const TreeRow: React.FC<{
  node: TreeNode;
  expanded: Set<string>;
  onToggle: (key: string) => void;
}> = ({ node, expanded, onToggle }) => {
  const { t } = useLanguage();
  const key = `${node.kind}-${node.id}`;
  const hasChildren = node.children.length > 0;
  const isOpen = expanded.has(key);
  const fallback =
    node.kind === 'vision'
      ? t('alignment.reports.unnamedVision')
      : node.kind === 'objective'
        ? t('alignment.reports.unnamedObjective')
        : node.kind === 'strategy'
          ? t('alignment.reports.unnamedStrategy')
          : t('alignment.reports.unnamedProject');

  return (
    <div>
      <div className="flex items-center gap-1.5 rounded-md px-1.5 py-1 hover:bg-[var(--color-secondary-bg)]">
        {hasChildren ? (
          <button
            type="button"
            onClick={() => onToggle(key)}
            className="p-0.5 text-[var(--color-secondary-text)]"
            aria-expanded={isOpen}
          >
            {isOpen ? <ChevronDownIcon className="w-3.5 h-3.5" /> : <ChevronRightIcon className="w-3.5 h-3.5" />}
          </button>
        ) : (
          <span className="w-4" />
        )}
        <span className={`inline-flex w-16 shrink-0 justify-center rounded px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${kindBadgeClass(node.kind)}`}>
          {t(`alignment.reports.tree.kind.${node.kind}`)}
        </span>
        <span className={`min-w-0 text-sm font-medium ${nodeColor(node)}`}>
          {node.name || fallback}
        </span>
      </div>
      {hasChildren && isOpen && (
        <div className="ml-5 border-l border-[var(--color-border)] pl-2">
          {node.children.map((child) => (
            <TreeRow key={`${child.kind}-${child.id}`} node={child} expanded={expanded} onToggle={onToggle} />
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Compact Vision → Objective → Strategy → Project tree for Alignment Reports.
 * Unlinked items sit under Unlinked so orphans are visible in the same view.
 */
export const AlignmentHierarchyTree: React.FC<AlignmentHierarchyTreeProps> = ({
  visions,
  orphans
}) => {
  const { t } = useLanguage();
  const linked = useMemo(() => buildLinkedForest(visions), [visions]);
  const linkedIds = useMemo(() => collectIds(linked, new Set()), [linked]);
  const unlinked = useMemo(() => orphanNodes(orphans, linkedIds), [orphans, linkedIds]);

  const defaultExpanded = useMemo(() => {
    const keys = new Set<string>();
    linked.forEach((vision) => {
      keys.add(`vision-${vision.id}`);
      vision.children.forEach((objective) => keys.add(`objective-${objective.id}`));
    });
    return keys;
  }, [linked]);

  const [expanded, setExpanded] = useState<Set<string>>(defaultExpanded);

  useEffect(() => {
    setExpanded(defaultExpanded);
  }, [defaultExpanded]);

  const toggle = (key: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  if (linked.length === 0 && unlinked.length === 0) {
    return <p className="text-sm text-[var(--color-secondary-text)]">{t('alignment.reports.tree.empty')}</p>;
  }

  return (
    <div className="pt-1">
      <div className="mb-2 flex flex-wrap gap-2">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-surface-text)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--color-error)]" />
          {t('alignment.reports.tree.legendOrphan')}
        </span>
        <span className="inline-flex items-center gap-1.5 rounded-full border border-[var(--color-border)] px-2 py-0.5 text-xs text-[var(--color-surface-text)]">
          <span className="h-1.5 w-1.5 rounded-full bg-purple-700" />
          {t('alignment.reports.tree.legendHandicap')}
        </span>
      </div>
      <div>
        {linked.map((node) => (
          <TreeRow key={`${node.kind}-${node.id}`} node={node} expanded={expanded} onToggle={toggle} />
        ))}
      </div>
      {unlinked.length > 0 && (
        <div className="mt-2 border-t border-[var(--color-border)] pt-2">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-[var(--color-secondary-text)]">
            {t('alignment.reports.tree.unlinked')}
          </div>
          {unlinked
            .slice()
            .sort((a, b) => KIND_ORDER.indexOf(a.kind) - KIND_ORDER.indexOf(b.kind))
            .map((node) => (
              <TreeRow key={`${node.kind}-${node.id}`} node={node} expanded={expanded} onToggle={toggle} />
            ))}
        </div>
      )}
    </div>
  );
};
